<script>
  import { onMount } from 'svelte'
  import { paperData, digestData, executionInfo, opsStatus } from '../../stores.js'
  import { getPaperDigest } from '../../api.js'
  import { HORIZON_TITLES, normalizeHorizonId } from '../../lib/horizon.js'
  import { fmtNum } from '../../lib/format.js'
  import { SITE_MODE } from '../../lib/hostSite.js'
  import CardGrid from '../../components/CardGrid.svelte'
  import HorizonTabs from '../../components/HorizonTabs.svelte'
  import LivePreflightBanner from '../../components/LivePreflightBanner.svelte'

  const KEYS = [
    'diagnosis',
    'research_div',
    'edge',
    'tempo',
    'kill',
    'vote_gate',
    'signals',
    'shadow',
    'brier',
    'edge_proven',
  ]

  const MAG_LABEL_RU = {
    weak: 'слабый',
    strong: 'сильный',
    none: 'нет',
  }
  const SLOPE_LABEL_RU = {
    agree: 'согласны',
    disagree: 'спор',
    slope_skip: 'slope SKIP',
    no_slope: 'нет slope',
  }

  const liveHost = SITE_MODE === 'live'

  let hz = '15m'
  let digestReq = 0

  $: pack = ($paperData && $paperData.horizons && $paperData.horizons[hz]) || null
  $: cards = (pack && pack.cards) || (hz === '15m' && $paperData && $paperData.cards) || {}
  $: health =
    (pack && pack.health) || (hz === '15m' && $paperData && $paperData.health) || null
  $: segs = (health && health.signal_stats && health.signal_stats.segments) || []
  $: paperSeg = (health && health.paper_segments) || null
  $: uiCopy = (pack && pack.ui_copy) || ($paperData && $paperData.ui_copy) || {}
  $: digestHz =
    ($digestData && $digestData.horizons && $digestData.horizons[hz]) || null
  $: digestText =
    (digestHz && digestHz.text) ||
    ($digestData && $digestData.horizon === hz && $digestData.text) ||
    ($digestData && hz === '15m' && $digestData.text) ||
    '—'
  $: hzTitle = HORIZON_TITLES[hz] || hz
  $: segBets = paperSeg ? Number(paperSeg.paper_bet_rows || 0) : 0
  $: segEmptyRu =
    uiCopy.paper_segments_empty ||
    (segBets <= 0
      ? 'Сегменты PAPER_BET: пока 0 ставок — каркас готов (час UTC / funding_mag / slope↔auth). Появятся с первой ставкой.'
      : `Сегменты PAPER_BET: ${segBets} ставок (WR/PnL по срезам).`)
  $: magRows = segBuckets(
    paperSeg && paperSeg.by_funding_mag_bucket,
    [
      { key: 'weak', bets: 0 },
      { key: 'strong', bets: 0 },
      { key: 'none', bets: 0 },
    ],
  )
  $: slopeRows = segBuckets(
    paperSeg && paperSeg.by_slope_vs_auth,
    [
      { key: 'agree', bets: 0 },
      { key: 'disagree', bets: 0 },
      { key: 'slope_skip', bets: 0 },
      { key: 'no_slope', bets: 0 },
    ],
  )
  $: hourRows = (() => {
    const hours = (paperSeg && paperSeg.by_hour_utc) || []
    if (segBets <= 0) {
      if (hours.length) return hours
      return Array.from({ length: 24 }, (_, i) => ({
        key: String(i),
        bets: 0,
        wins: 0,
        losses: 0,
      }))
    }
    const nonzero = hours.filter((b) => Number(b.bets || 0) > 0)
    return nonzero.length ? nonzero : [{ key: '—', bets: 0, wins: 0, losses: 0 }]
  })()

  $: ex = $executionInfo || {}
  $: liveUnit = (() => {
    const units = ($opsStatus && $opsStatus.units) || []
    const u = units.find((x) => x && (x.unit === 'pmbot-live-15m' || x.name === 'pmbot-live-15m'))
    if (!u) return null
    return `${u.active_state || '—'}${u.sub_state ? '/' + u.sub_state : ''}`
  })()

  function segBuckets(arr, fallback) {
    return Array.isArray(arr) && arr.length ? arr : fallback
  }

  function bucketLabel(key, map) {
    const k = String(key != null ? key : '')
    if (map && map[k]) return map[k]
    if (k.length === 1) return `0${k}`
    return k || '—'
  }

  function segWr(b) {
    if (!b || b.win_rate == null || !Number.isFinite(Number(b.win_rate))) return '—'
    return `${Math.round(Number(b.win_rate) * 100)}%`
  }

  function segPnl(b) {
    if (!b || b.pnl_est_sum == null) return '—'
    return fmtNum(b.pnl_est_sum, { signed: true, digits: 3 })
  }

  async function loadDigest(forHz = hz) {
    if (liveHost) return
    const id = normalizeHorizonId(forHz)
    const reqId = ++digestReq
    const { res, data } = await getPaperDigest({ horizon: id })
    if (reqId !== digestReq) return
    if (res.ok && data) digestData.set(data)
  }

  function onHzChange(next) {
    if (liveHost) return
    const id = normalizeHorizonId(next)
    if (id === hz) return
    hz = id
    loadDigest(id)
  }

  onMount(() => {
    loadDigest(hz)
  })
</script>

{#if liveHost}
  <p class="pm-meta" style="margin:0 0 0.85rem">
    Диагностика · LIVE 15m · unit pmbot-live-15m
    {#if liveUnit}
      · {liveUnit}
    {/if}
  </p>
  <LivePreflightBanner force={true} />
  <section class="pm-surface" style="margin-top:1rem" aria-label="Live status">
    <dl class="pm-kv">
      <div>
        <dt>pUSD</dt>
        <dd class="mono">
          {ex.onchain_pusd_balance != null ? Number(ex.onchain_pusd_balance).toFixed(4) : '—'}
        </dd>
      </div>
      <div>
        <dt>POL</dt>
        <dd class="mono">
          {ex.onchain_pol_balance != null ? Number(ex.onchain_pol_balance).toFixed(4) : '—'}
        </dd>
      </div>
      <div>
        <dt>Ордера</dt>
        <dd>{ex.live_orders_armed ? 'armed' : 'не armed'}</dd>
      </div>
      <div>
        <dt>Kill</dt>
        <dd>{ex.kill_switch_active ? 'активен' : 'выкл'}</dd>
      </div>
      <div>
        <dt>Ставки (live)</dt>
        <dd class="mono">
          {(ex.live_bets && ex.live_bets.bet_count) != null ? ex.live_bets.bet_count : '—'}
        </dd>
      </div>
    </dl>
    {#if ex.safety_ru}
      <p class="pm-meta" style="margin-top:0.75rem">{ex.safety_ru}</p>
    {/if}
  </section>
{:else}
<div class="diag-hz">
  <HorizonTabs value={hz} includeAll={false} label="Горизонт диагностики" on:change={(e) => onHzChange(e.detail)} />
</div>

<p class="pm-meta" style="margin:0 0 0.85rem">
  Карточки · {hzTitle}
  {#if pack && pack.unit}
    · {pack.unit}
  {/if}
</p>

{#if KEYS.some((k) => cards && cards[k])}
  <CardGrid {cards} keys={KEYS} />
{:else}
  <p class="pm-empty">Нет карточек для {hz} — health/CSV ещё пустые или unit не пишет.</p>
{/if}

{#if segs.length}
  <section class="pm-surface" style="margin-top:1.25rem" aria-label="Сигналы">
    <h2 class="pm-section-head" style="display:block;margin:0 0 0.75rem;font-size:1rem">
      Сигналы (shadow) · {hz}
    </h2>
    <table class="pm-table">
      <thead>
        <tr>
          <th>id</th>
          <th>UP</th>
          <th>DOWN</th>
          <th>SKIP</th>
          <th>hypWR</th>
          <th>W/L</th>
        </tr>
      </thead>
      <tbody>
        {#each segs as s}
          <tr>
            <td>{s.id ?? '—'}</td>
            <td>{s.up ?? 0}</td>
            <td>{s.down ?? 0}</td>
            <td>{s.skip ?? 0}</td>
            <td>{s.hyp_win_rate != null ? Math.round(Number(s.hyp_win_rate) * 100) + '%' : '—'}</td>
            <td>{(s.hyp_wins ?? 0) + '/' + (s.hyp_losses ?? 0)}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </section>
{/if}

{#if paperSeg}
  <section class="pm-surface diag-seg" style="margin-top:1.25rem" aria-label="Сегменты PAPER_BET">
    <h2 style="margin:0 0 0.35rem;font-size:1rem">Сегменты PAPER_BET · {hz}</h2>
    <p class="pm-meta" class:diag-seg__ok={segBets > 0}>{segEmptyRu}</p>

    <div class="diag-seg__grid">
      <div>
        <h3 class="diag-seg__sub">Funding mag</h3>
        <table class="pm-table">
          <thead>
            <tr>
              <th>срез</th>
              <th>bets</th>
              <th>W/L</th>
              <th>WR</th>
              <th>PnL</th>
            </tr>
          </thead>
          <tbody>
            {#each magRows as b}
              <tr class:is-empty={!Number(b.bets || 0)}>
                <td>{bucketLabel(b.key, MAG_LABEL_RU)}</td>
                <td>{b.bets ?? 0}</td>
                <td>{(b.wins ?? 0) + '/' + (b.losses ?? 0)}</td>
                <td>{segWr(b)}</td>
                <td>{segPnl(b)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div>
        <h3 class="diag-seg__sub">Slope ↔ auth</h3>
        <table class="pm-table">
          <thead>
            <tr>
              <th>срез</th>
              <th>bets</th>
              <th>W/L</th>
              <th>WR</th>
              <th>PnL</th>
            </tr>
          </thead>
          <tbody>
            {#each slopeRows as b}
              <tr class:is-empty={!Number(b.bets || 0)}>
                <td>{bucketLabel(b.key, SLOPE_LABEL_RU)}</td>
                <td>{b.bets ?? 0}</td>
                <td>{(b.wins ?? 0) + '/' + (b.losses ?? 0)}</td>
                <td>{segWr(b)}</td>
                <td>{segPnl(b)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </div>

    <h3 class="diag-seg__sub" style="margin-top:1rem">Час UTC</h3>
    <p class="pm-meta">
      {segBets <= 0
        ? 'При 0 ставок — все 24 часа пустые; ненулевые появятся после PAPER_BET.'
        : 'Показаны только часы с bets > 0.'}
    </p>
    <div class="diag-seg__hours">
      <table class="pm-table">
        <thead>
          <tr>
            <th>час</th>
            <th>bets</th>
            <th>W/L</th>
            <th>WR</th>
            <th>PnL</th>
          </tr>
        </thead>
        <tbody>
          {#each hourRows as b}
            <tr class:is-empty={!Number(b.bets || 0)}>
              <td>{bucketLabel(b.key, null)}</td>
              <td>{b.bets ?? 0}</td>
              <td>{(b.wins ?? 0) + '/' + (b.losses ?? 0)}</td>
              <td>{segWr(b)}</td>
              <td>{segPnl(b)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  </section>
{/if}

<section class="pm-surface" style="margin-top:1.25rem" aria-label="Digest">
  <div class="pm-section-head">
    <h2>Digest / health · {hz}</h2>
    <button type="button" class="pm-btn" on:click={() => loadDigest(hz)}>Обновить</button>
  </div>
  <p class="pm-meta">
    {$digestData
      ? ($digestData.source || (digestHz && digestHz.source) || '') +
        (digestHz && digestHz.name
          ? ' · ' + digestHz.name
          : $digestData.name
            ? ' · ' + $digestData.name
            : '') +
        ((digestHz && digestHz.truncated) || $digestData.truncated ? ' · truncated' : '')
      : 'Загрузка…'}
  </p>
  <pre class="pm-pre">{digestText}</pre>
</section>
{/if}

<style>
  .diag-hz {
    margin: 0 0 0.85rem;
  }
  .diag-seg__ok {
    color: var(--pm-ink-soft);
  }
  .diag-seg__grid {
    display: grid;
    gap: 1rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin-top: 0.75rem;
  }
  @media (max-width: 800px) {
    .diag-seg__grid {
      grid-template-columns: 1fr;
    }
  }
  .diag-seg__sub {
    margin: 0 0 0.45rem;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .diag-seg__hours {
    max-height: 16rem;
    overflow: auto;
    margin-top: 0.35rem;
  }
  .diag-seg :global(tr.is-empty td) {
    opacity: 0.55;
  }
</style>
